import autobind from 'class-autobind';

import ABCDReduction from 'atg/api/ABCDReduction';
import * as device from 'atg/utils/device';
import * as Utils from 'atg/utils/Utils';
import StickyTemplate from 'atg/vendor/StickyTemplate/1.0/StickyTemplate';
import { settings } from 'shared/redux/selectors';
import { getStore } from 'shared/redux/store';
import * as Text from 'shared/utils/Text';

import { createHelpIcon } from '../HelpIcon';
import BaseView from './BaseView';

const $ = window.$;

export default class ABCDReductionView extends BaseView {
  constructor() {
    super(...arguments);
    autobind(this, ABCDReductionView.prototype);
  }

  getGridClass() {
    return 'view-ABCD-reduction';
  }

  // ---------------------------------------- MENU ----------------------------------------

  getViewTitle() {
    return Text.get('view-abcd', this.getNumber(ABCDReductionView));
  }

  getMenuTitle() {
    return Text.get('view-abcd', this.getNumber(ABCDReductionView));
  }

  getMenuTogglable() {
    return true;
  }

  getMenuDeletable() {
    return true;
  }

  // ---------------------------------------- GRID ----------------------------------------
  onRowWasUpdated() {}
  getGridCellInfo(position, rowCache) {
    const api = this.getAPI();

    let race;
    let betRow;
    let gameRow;
    let resultRow;
    let betConfig;

    if (rowCache) {
      race = rowCache.race;
      betRow = rowCache.betRow;
      gameRow = rowCache.gameRow;
      resultRow = rowCache.resultRow;
      betConfig = rowCache.betConfig;
    } else {
      // eslint-disable-next-line no-param-reassign
      rowCache = {};

      race = rowCache.race = api.raceInformation.getRace(position.row);
      betRow = rowCache.betRow = api.betInformation.getRow(position.row);
      gameRow = rowCache.gameRow = api.gameInformation.getRow(position.row);
      resultRow = rowCache.resultRow = api.resultInformation.getRow(
        position.row,
      );
      betConfig = rowCache.betConfig = api.betInformation.getBetConfig();
    }

    const startNumber = betRow
      ? betRow.getStartNumberAtPosition(position.column)
      : position.column + 1;

    const startIndex = startNumber - 1;
    const start = race.getStart(startIndex);
    const info = {
      rowCache: rowCache,
    };

    if (start) {
      info.number = start.getStartNumber();

      const horse = start.getHorse();
      if (horse) {
        info.horseName = horse.getName();
      }

      const jockey = start.getJockey();
      if (jockey) {
        info.jockeyTrainerName = jockey.getName();
      }

      const originalJockey = start.getOriginalJockey();
      if (originalJockey) {
        info.originalJockey = originalJockey.getName();
      }
    }

    if (
      resultRow &&
      settings.showRankingResults(getStore().getState()) &&
      betConfig.getShowResultRow(position.row)
    ) {
      info.winner = resultRow.isWinner(startIndex);
    }

    const gameRowCellInfo = this.getGameRowCellInfo(gameRow, startIndex);
    Object.assign(info, gameRowCellInfo);

    if (betRow) {
      info.bottomBlue = betRow.getABCDReductionLetter(position.column);
    }

    return info;
  }

  onGridCellTapped(position) {
    this.getGrid().highlightCell(position);

    const buttons = [
      {
        icon: 'button-last',
        onPress: this.onGridLastButtonPressed,
      },
      {
        icon: 'button-minus',
        onPress: this.onGridMinusButtonPressed,
      },
      {
        icon: 'button-plus',
        onPress: this.onGridPlusButtonPressed,
      },
      ...this.makeHorseButtons(position),
    ];

    if (!device.isTouch()) {
      this.onGridNumpadButtonPressed(position);
    }
    this.getGrid().showCellButtons(position, buttons);
  }

  onGridPlusButtonPressed(position) {
    const willShowLockedAlert = this.showAlertWhenLocked();
    const grid = this.getGrid();

    if (willShowLockedAlert) {
      grid.closeHighlight();
    } else {
      const betRow = this.getAPI().betInformation.getRow(position.row);
      const letter = betRow.getABCDReductionLetter(position.column);
      const tmpLetter = String.fromCodePoint(
        Math.min(letter.codePointAt(0) + 1, 72),
      );

      betRow.setABCDReductionLetter(position.column, tmpLetter);
      grid.updateRow(position.row);
      this.getAPI().scheduleBetUpdate();
    }
  }

  onGridMinusButtonPressed(position) {
    const willShowLockedAlert = this.showAlertWhenLocked();
    const grid = this.getGrid();

    if (willShowLockedAlert) {
      grid.closeHighlight();
    } else {
      const betRow = this.getAPI().betInformation.getRow(position.row);
      const letter = betRow.getABCDReductionLetter(position.column);
      const tmpLetter = String.fromCodePoint(
        Math.max(letter.codePointAt(0) - 1, 65),
      );

      betRow.setABCDReductionLetter(position.column, tmpLetter);
      grid.updateRow(position.row);
      this.getAPI().scheduleBetUpdate();
    }
  }

  onGridNumpadButtonPressed(position) {
    if (this.isBetLocked()) {
      return;
    }
    const betRow = this.getAPI().betInformation.getRow(position.row);
    const letter = betRow.getABCDReductionLetter(position.column);

    this.getGrid().showLetterInput(position, letter);

    this.getAPI().scheduleBetUpdate();
  }

  // ---------------------------------------- DATA ----------------------------------------

  getABCDReduction() {
    let reduction = this.getAPI()
      .betInformation.getReductionSet()
      .getABCDReduction();
    if (!reduction) {
      reduction = new ABCDReduction();
    }

    return reduction;
  }

  getItem() {
    return this.getABCDReduction();
  }

  deleteItem() {
    const api = this.getAPI();

    const numberOfRows = api.betInformation.getNumberOfRows();
    for (let row = 0; row < numberOfRows; row++) {
      api.betInformation.getRow(row).deleteABCDReduction();
    }

    super.deleteItem();
  }

  // ---------------------------------------- BOTTOM INFO DELEGATE METHODS ----------------------------------------

  getBottomInfoContent() {
    const $conditions = (this.$conditions = StickyTemplate.jq(
      'bottom-conditions-abcd-reduction',
    ));
    $conditions.find('form').event('submit', this.onFormSubmit);
    this.updateConditionsABCDMethod();

    return $conditions;
  }

  // ---------------------------------------- ABCD SORT ----------------------------------------

  updateConditionsABCDMethod() {
    const reduction = this.getABCDReduction();
    const numRaces = this.getAPI().raceInformation.getNumberOfRaces();
    const numberOfSections = Math.max(1, reduction.getNumberOfABCDMethods());
    const $wrapper = this.$conditions.find('form');

    let $section;

    $wrapper.empty();
    // METHODS
    for (let n = 0; n < numberOfSections; n++) {
      const abcdMethod = reduction.getABCDMethod(n);
      const initialAdd = abcdMethod == null;

      $section = StickyTemplate.jq('bottom-conditions-abcd-reduction-method', {
        '%abcd-reduction-method%': `${Text.get(
          'bottom-conditions-abcd-reduction-method',
        )} ${Number.parseInt(n + 1, 10)}`,
        '%add-method%': Text.get('bottom-conditions-abcd-reduction-add-method'),
        '%method-divider%': Text.get(
          'bottom-conditions-abcd-reduction-method-divider',
        ),
        '%method%': n,
        '%rows-with-abcd-method%': Text.get(
          'bottom-conditions-rows-with-abcd-method',
          n + 1,
        ),
        '%should-be-submitted%': Text.get(
          'bottom-conditions-factor-to-be-submitted',
        ),
        '%times%': Text.get('bottom-conditions-factor-times'),
      });

      const $rows = $section.find('.rows');

      if (abcdMethod && abcdMethod.getNumberOfABCDMethodRows() === 0) {
        this.addABCDMethodRow(abcdMethod);
        this.addABCDMethodRow(abcdMethod);
        this.addABCDMethodRow(abcdMethod);
      }

      const numberOfRows = initialAdd
        ? 4
        : abcdMethod.getNumberOfABCDMethodRows() > 0
          ? abcdMethod.getNumberOfABCDMethodRows()
          : 1;

      // ROWS
      for (let i = 0; i < numberOfRows; i++) {
        const abcdMethodRow = initialAdd
          ? false
          : abcdMethod.getABCDMethodRow(i);

        const $row = StickyTemplate.jq(
          'bottom-conditions-abcd-reduction-method-row',
          {
            '%Min%': Text.get('bottom-conditions-Min'),
            '%max%': Text.get('bottom-conditions-max'),
            '%num-races%': numRaces,
            '%horses-in-section%': Text.get(
              'bottom-conditions-abcd-horses-in-section',
            ),
            '%method%': n,
            '%row%': i,
          },
        );

        this.addLetterInput($row);

        let legNumbers;

        if (abcdMethodRow) {
          let cLetters = abcdMethodRow.getLetter();
          if (cLetters == null || cLetters.codePointAt(0) < 10) {
            cLetters = 'A';
          }

          $row
            .find('.min-points')
            .val(Utils.negativeBlankToValue(abcdMethodRow.getMinPoints()));
          $row
            .find('.max-points')
            .val(Utils.negativeBlankToValue(abcdMethodRow.getMaxPoints()));
          $row.find('.abcd-select').val(cLetters);
          legNumbers = abcdMethodRow.getLegNumbers();
        } else {
          const letters = this.getLetterOptions();
          legNumbers = this.getLegNumbers();
          $row.find('.abcd-select').val(letters[i]);
        }

        if (i === 0) {
          $row.append(
            createHelpIcon({
              header: Text.get('help-abcd-reduction-races-header'),
              body: Text.get('help-abcd-reduction-races-body'),
            }),
          );
        }

        this.addLegNumbersInputs($row, legNumbers);
        $rows.append($row);
      }

      const emptyMethod = initialAdd || abcdMethod.isEmpty();
      $section
        .find('.factor')
        .val(emptyMethod ? '' : abcdMethod.getFactor())
        .prop('disabled', emptyMethod);

      $section.find('.abcd-factor').append(
        createHelpIcon({
          header: Text.get('help-abcd-reduction-factor-header'),
          body: Text.get('help-abcd-reduction-factor-body'),
        }),
      );

      $wrapper.append($section);
    }

    $section.find('.button.method.plus .wrapper').append(
      createHelpIcon({
        header: Text.get('help-abcd-reduction-add-header'),
        body: Text.get('help-abcd-reduction-add-body'),
      }),
    );

    $wrapper
      .find('input[type=number]')
      .reevent('input', this.onConditionsChanged);
    $wrapper
      .find('input:not([type=number]), select')
      .reevent('change', this.onConditionsChanged);
    $wrapper
      .find('.button.method.plus .align.x.y')
      .reevent('click', this.onAddABCDMethodPressed);
    $wrapper
      .find('.button.method-row.plus')
      .reevent('click', this.onAddABCDMethodRowPressed);
    $wrapper
      .find('.button.method.delete')
      .reevent('click', this.onDeleteABCDMethodPressed);
    $wrapper
      .find('.button.method-row.delete')
      .reevent('click', this.onDeleteABCDMethodRowPressed);

    this.attachNegativeBlankEvents($wrapper);
  }

  getLetterOptions() {
    return ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
  }

  addLetterInput($parent) {
    const sel = 'select.abcd-select';
    const $select = $parent.find(sel).addBack(sel);
    const letters = this.getLetterOptions();
    const options = [];

    for (const letter of letters) {
      options.push({
        value: letter,
        text: letter,
      });
    }

    this.setSelectOptions($select, options);
  }

  getNextMethodRowLetter(abcdMethod) {
    const numberOfRows = abcdMethod.getNumberOfABCDMethodRows();
    const lastRow = abcdMethod.getABCDMethodRow(numberOfRows - 1);
    const lastRowLetter = lastRow.getLetter();

    const letters = this.getLetterOptions();
    let lastRowLetterIndex = letters.indexOf(lastRowLetter);
    if (lastRowLetterIndex < 0) {
      lastRowLetterIndex = 0;
    }

    let nextRowLetterIndex = lastRowLetterIndex + 1;
    if (nextRowLetterIndex >= letters.length) {
      nextRowLetterIndex = letters.length - 1;
    }

    const currentLetter = letters[nextRowLetterIndex];
    return currentLetter;
  }

  addABCDMethodRow(_abcdMethod) {
    const abcdMethod = _abcdMethod || this.getABCDReduction().addABCDMethod();

    if (abcdMethod.getNumberOfABCDMethodRows() === 0) {
      abcdMethod.addABCDMethodRow().setLegNumbers(this.getLegNumbers());
    }

    const nextRowLetter = this.getNextMethodRowLetter(abcdMethod);
    const methodRow = abcdMethod.addABCDMethodRow();
    methodRow.setLegNumbers(this.getLegNumbers());
    methodRow.setLetter(nextRowLetter);

    return abcdMethod;
  }

  onAddABCDMethodPressed() {
    const reduction = this.getABCDReduction();
    if (reduction.getNumberOfABCDMethods() === 0) {
      reduction.addABCDMethod();
    }

    reduction.addABCDMethod();

    this.getAPI().scheduleBetUpdate();

    this.updateConditionsABCDMethod();

    const $method = this.$conditions.find('.abcd-method:last-child');
    $method.hide();
    $method.slideDown(300);
  }

  onAddABCDMethodRowPressed(event) {
    const index = $(event.currentTarget).closest('.abcd-method').index();
    let methodBlock = this.getABCDReduction().getABCDMethod(index);

    // Fix for when no abcd reduction condition has been created yet.
    // This happens when no input has been made to the four initial rows and
    // the user clicks to add a fifth row. At this stage we need to
    // make the system actually create the first four rows first and then we
    // can add a fifth. We slightly abuse the onConditionsChanged function
    // to make this happen.
    if (methodBlock == null) {
      this.onConditionsChanged();
      methodBlock = this.getABCDReduction().getABCDMethod(index);
    }

    this.addABCDMethodRow(methodBlock);

    this.getAPI().scheduleBetUpdate();
    this.updateConditionsABCDMethod();
    if (1 < methodBlock.getNumberOfABCDMethodRows()) {
      const $row = this.$conditions.find(
        `.abcd-method:eq(${index}) .row:last-child`,
      );
      $row.hide();
      $row.slideDown(300);
    }
  }

  onDeleteABCDMethodPressed(event) {
    const $target = $(event.currentTarget);
    const $method = $target.closest('.abcd-method');
    const index = $method.index();
    const reduction = this.getABCDReduction();

    reduction.deleteABCDMethod(index);
    this.getAPI().scheduleBetUpdate();

    if (reduction.getNumberOfABCDMethods() < 1) {
      this.updateConditionsABCDMethod();
    } else {
      $method.slideUp(
        300,
        function () {
          this.updateConditionsABCDMethod();
        }.bind(this),
      );
    }
  }

  onDeleteABCDMethodRowPressed(event) {
    const $target = $(event.currentTarget);
    const $method = $target.closest('.abcd-method');
    const methodIndex = $method.index();

    const $row = $target.closest('.row');
    const rowIndex = $row.index();

    const reduction = this.getABCDReduction();
    const abcdMethod = reduction.getABCDMethod(methodIndex);

    if (abcdMethod) {
      abcdMethod.deleteABCDMethodRow(rowIndex);
      this.getAPI().scheduleBetUpdate();
      if (abcdMethod.getNumberOfABCDMethodRows() < 1) {
        this.updateConditionsABCDMethod();
      } else {
        $row.slideUp(
          300,
          function () {
            this.updateConditionsABCDMethod();
          }.bind(this),
        );
      }
    }
  }

  // ---------------------------------------- FORM TO DATA ----------------------------------------

  onConditionsChanged() {
    const $form = this.$conditions.find('form');
    const formData = $form.formData();
    const reduction = this.getABCDReduction();

    const numberOfFormMethods = formData.abcdMethod
      ? formData.abcdMethod.length
      : 0;
    const numberOfMethods = reduction.getNumberOfABCDMethods();

    for (let n = 0; n < numberOfFormMethods; n++) {
      if (n >= numberOfMethods) {
        reduction.addABCDMethod();
      }

      const abcdMethod = reduction.getABCDMethod(n);
      const numberOfFormMethodRows = formData.abcdMethod[n].rows.length;
      const numberOfMethodsRows = abcdMethod.getNumberOfABCDMethodRows();

      for (let i = 0; i < numberOfFormMethodRows; i++) {
        if (i >= numberOfMethodsRows) {
          abcdMethod.addABCDMethodRow();
        }

        const abcdMethodRow = abcdMethod.getABCDMethodRow(i);
        const abcdMethodRowData = formData.abcdMethod[n].rows[i];

        abcdMethodRow.setMinPoints(
          Utils.valueToNegativeBlank(abcdMethodRowData.minPoints),
        );
        abcdMethodRow.setMaxPoints(
          Utils.valueToNegativeBlank(abcdMethodRowData.maxPoints),
        );
        abcdMethodRow.setLetter(abcdMethodRowData.letter);
        abcdMethodRow.setLegNumbers(abcdMethodRowData.legNumbers);
      }

      while (numberOfFormMethodRows < abcdMethod.getNumberOfABCDMethodRows()) {
        abcdMethod.deleteABCDMethodRow(
          abcdMethod.getNumberOfABCDMethodRows() - 1,
        );
      }

      const emptyMethod = abcdMethod.isEmpty();
      abcdMethod.setFactor(emptyMethod ? 1 : formData.abcdMethod[n].factor);

      // Depending on emptyMethod, adjust factor input field
      const $factorInput = $(`input[name="abcdMethod[${n}][factor]"]`);
      if ($factorInput) {
        $factorInput.prop('disabled', emptyMethod);
        if (emptyMethod) {
          $factorInput.val('');
        } else if (!$factorInput.val() && !$factorInput.is(':focus')) {
          $factorInput.val('1');
        }
      }
    }

    while (numberOfFormMethods < reduction.getNumberOfABCDMethods()) {
      reduction.deleteABCDMethod(reduction.getNumberOfABCDMethods() - 1);
    }

    this.getAPI().scheduleBetUpdate();
  }

  abcdReductionLetterChanged(position, value) {
    const betRow = this.getAPI().betInformation.getRow(position.row);
    betRow.setABCDReductionLetter(position.column, value);
  }
}
